import { useState, useRef, MouseEventHandler, useMemo } from 'react';
import { ClickAwayListener } from '@mui/material';

import { QuillWrapper } from '@labradorsports/components';

import { playbookActions, routerActions } from '../../../store/index.js';
import { useDispatcher } from '../../hooks/index.js';
import { PersonnelCard } from '../../interface/index.js';
import { useNavigateWithQuery } from '../../providers/index.js';
import { useContentEditor } from '../content-editor/content-editor-context.js';

interface Props {
    value?: string;
    formats?: any;
    addedPlayers?: any[];
    disableClicks?: boolean;
    fullscreenPlayPreview?: boolean;
}

const ContentViewer: FC<Props> = ({
    children,
    value = '',
    addedPlayers,
    disableClicks = false,
    fullscreenPlayPreview,
    ...props
}) => {
    const openPlay = useDispatcher(routerActions.OpenPlay);
    const overrideMenuWithBack = useDispatcher(playbookActions.OverrideMenuWithBack);

    const navigate = useNavigateWithQuery();
    const { showPlayPreview } = useContentEditor();
    const rosterPlayersRef = useRef(null);
    const elementRef = useRef(null);
    const [selectedRosterPlayer, setSelectedRosterPlayer] = useState(null);
    const classNames = ['content-viewer'];

    rosterPlayersRef.current = addedPlayers;

    const modulesRef = useMemo(
        () => ({
            rosterPlayer: {
                getAddedPlayers: () => rosterPlayersRef.current ?? [],
            },
        }),
        []
    );

    const openPostPlay = (href: string) => {
        // Ensure there is an & to match last param on
        const matchHref = `${href}&`;
        const playIdMatch = matchHref.match(/[&?]p=(.*?)&/);

        const playId = playIdMatch?.[1];

        if (fullscreenPlayPreview) {
            openPlay(playId);
            showPlayPreview(playId);
        } else {
            navigate('/playbook/team', {
                playId,
            });
        }
    };

    const showPlayerTooltip = (target: HTMLElement, hover = false) => {
        const jersey = target.textContent.replace('#', '');
        const player = addedPlayers.find((plr) => plr.jersey === jersey);

        if (player) {
            const width = elementRef.current.offsetWidth;
            const style: any = {};

            if (target.offsetLeft < width / 2) {
                style.left = `${Math.max(target.offsetLeft - 50, 0)}px`;
            } else {
                style.right = `${width - target.offsetLeft - 50}px`;
            }

            if (target.getBoundingClientRect().top > 0.7 * window.innerHeight) {
                style.bottom = `${elementRef.current.offsetHeight - target.offsetTop}px`;
            } else {
                style.top = `calc(${target.offsetTop + 23}px + 0.5rem)`;
            }

            setSelectedRosterPlayer({
                style,
                player,
                hover,
            });
        }
    };

    const onClick: MouseEventHandler<HTMLDivElement> = (evt) => {
        evt.stopPropagation();
        const target = evt.target as HTMLElement;
        const playTarget = [target, target.parentElement].find((el) => el.hasAttribute('data-href'));
        if (playTarget) {
            overrideMenuWithBack(true);
            openPostPlay(playTarget.getAttribute('data-href'));
        }

        if (addedPlayers && target.className?.includes('roster-player')) {
            showPlayerTooltip(target);
        }
    };

    const onHover = (evt: any) => {
        if (addedPlayers) {
            if (!selectedRosterPlayer && evt.target.className?.includes('roster-player')) {
                showPlayerTooltip(evt.target, true);
            }

            if (
                selectedRosterPlayer &&
                !evt.target.className?.includes('roster-player') &&
                selectedRosterPlayer.hover
            ) {
                setSelectedRosterPlayer(null);
            }
        }
    };

    const closeRosterPlayerTooltip = (evt: any) => {
        if (evt.type !== 'mouseleave' || selectedRosterPlayer?.hover) {
            setSelectedRosterPlayer(null);
        }
    };

    return (
        <div className={classNames.join(' ')} ref={elementRef}>
            {selectedRosterPlayer ? (
                <ClickAwayListener onClickAway={closeRosterPlayerTooltip}>
                    <div className="personnel-tooltip" style={selectedRosterPlayer.style}>
                        <PersonnelCard person={selectedRosterPlayer.player} readOnly />
                        <span className="close-button" onClick={closeRosterPlayerTooltip}>
                            &times;
                        </span>
                    </div>
                </ClickAwayListener>
            ) : null}
            <QuillWrapper
                value={value}
                disableClicks={disableClicks}
                onClick={onClick}
                onMouseMove={onHover}
                onMouseLeave={closeRosterPlayerTooltip}
                modules={modulesRef}
                readOnly
                {...props}
            />
        </div>
    );
};

export default ContentViewer;
